import { useLayoutEffect, useState, type RefObject } from "react";

/**
 * Whether a horizontal scroller holds more than its width shows, so a desktop
 * "next" button appears only when there is something to scroll to.
 *
 * The children are measured as well as the row: a chip added after the
 * server answers, or widened when the webfont lands, grows `scrollWidth`
 * without resizing the row itself.
 */
export function useHorizontalOverflow(rowRef: RefObject<HTMLElement>): boolean {
  const [overflows, setOverflows] = useState(false);

  useLayoutEffect(() => {
    // A mobile bar renders no scroll row: it has no button to show.
    const row = rowRef.current;
    if (row === null) return;

    function update(): void {
      if (row === null) return;
      setOverflows(row.scrollWidth - row.clientWidth > 1);
    }

    const resizeObserver = new ResizeObserver(update);
    function observeAll(): void {
      if (row === null) return;
      resizeObserver.disconnect();
      resizeObserver.observe(row);
      for (const child of row.children) resizeObserver.observe(child);
      update();
    }
    observeAll();
    const mutationObserver = new MutationObserver(observeAll);
    mutationObserver.observe(row, { childList: true });
    return () => {
      mutationObserver.disconnect();
      resizeObserver.disconnect();
    };
  }, [rowRef]);

  return overflows;
}
